/**
 * Revenue & Rates workspace (M8) — the professional PMS pricing workspace.
 *
 * ONE module with six internal tabs (not six sidebar modules):
 *   1. Overview       — property performance KPIs (gross revenue, occupancy,
 *                       units) rolled up + a per-property table.
 *   2. Rate Calendar  — THE core screen: a rooms x dates grid with per-cell
 *                       rate + stop-sell edit (popover) and a bulk-update tool
 *                       (date range, day-of-week filter, +% or set rate, with a
 *                       preview). Every save auto-syncs to Channex (no manual
 *                       Sync). Restriction indicators (M2/SS/CTA/CTD).
 *   3. Rate Plans     — list every rate plan across a property's units + create.
 *   4. Restrictions   — edit rate-plan min/max stay + CTA/CTD (auto-sync).
 *   5. Pricing Rules  — the yield-rule engine (occupancy / lead time / weekend /
 *                       LOS / min-max clamps), priority, guardrails (property
 *                       min/max price), and a Rate Simulator (pricing preview).
 *   6. Sync Health    — per-property Channex health + recent change history.
 *
 * A GLOBAL property context selector scopes every tab; it auto-selects the sole
 * property when there is one and persists across tabs (component state). All
 * backend access goes through the fetch-based `../services.js` layer (web
 * boundary: no `@avos/core-api` import). Money arrives as integer-minor STRINGS
 * and is shown via `formatMoney(Number(x), currency)`. API error envelopes
 * surface through the shared accessible {@link UserMessage} component.
 *
 * SSR-safe: the initial render is a pure loading state; every network call and
 * any `Date` read runs inside an effect or an event handler.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactElement,
} from "react";
import {
  UserMessage,
  toUserMessage,
  type UserMessageModel,
} from "../../components/UserMessage.js";
import {
  apiListProperties,
  apiRevenueOverview,
  apiRateCalendar,
  apiSaveCalendarCell,
  apiBatchUpdateCalendar,
  apiInventoryCalendar,
  apiInventorySummary,
  apiInventorySetAvailable,
  apiInventoryReopen,
  apiCreateInventoryBlock,
  apiInventoryBatch,
  apiRevenueListRatePlans,
  apiSaveRestrictions,
  apiGetGuardrails,
  apiSaveGuardrails,
  apiSyncHealth,
  apiRevenueHistory,
  apiChannexDiagnostics,
  apiAriJobs,
  apiFullReconciliation,
  apiFullReconciliationHistory,
  apiPayloadPreview,
  apiRetrySync,
  apiListYieldRules,
  apiCreateYieldRule,
  apiSetRuleEnabled,
  apiDeleteYieldRule,
  apiSimulateRate,
  asError,
  formatMoney,
  parseMoneyToMinor,
  type Property,
  type RevenueOverview,
  type RateCalendar,
  type CalendarRow,
  type InventoryCalendar,
  type InventoryRow,
  type InventoryCell,
  type InventoryBatchLine,
  type BatchLineSummary,
  type BatchSummary,
  type RatePlanRow,
  type Guardrails,
  type SyncHealthRow,
  type HistoryItem,
  type YieldRule,
  type YieldRuleKind,
  type RateSimulationResult,
} from "../services.js";
import type { FullReconciliationRun, PayloadPreview } from "../services.js";

// ===========================================================================
// Shared helpers
// ===========================================================================

/** The six workspace tabs. */
type TabKey =
  | "overview"
  | "inventory"
  | "calendar"
  | "rate-plans"
  | "restrictions"
  | "rules"
  | "sync";

const TABS: readonly { readonly key: TabKey; readonly label: string }[] = [
  { key: "overview", label: "Overview" },
  { key: "inventory", label: "Inventory" },
  { key: "calendar", label: "Rate Calendar" },
  { key: "rate-plans", label: "Rate Plans" },
  { key: "restrictions", label: "Restrictions" },
  { key: "rules", label: "Pricing Rules" },
  { key: "sync", label: "Sync Health" },
];

/** The tab keys composed into the REVENUE screen (business performance only). */
export const REVENUE_TAB_KEYS: readonly TabKey[] = ["overview"];

/** The tab keys composed into the INVENTORY & RATES screen (operations). */
export const INVENTORY_RATES_TAB_KEYS: readonly TabKey[] = [
  "inventory", "calendar", "rate-plans", "restrictions", "rules", "sync",
];

/** Human labels for a set of tab keys (for tests / nav display). */
export function tabLabelsFor(keys: readonly TabKey[]): readonly string[] {
  return TABS.filter((t) => keys.includes(t.key)).map((t) => t.label);
}

/** The yield-rule kinds, with human labels. */
const RULE_KINDS: readonly { readonly value: YieldRuleKind; readonly label: string }[] = [
  { value: "occupancy", label: "Occupancy ≥" },
  { value: "lead_time", label: "Lead time (days) ≥" },
  { value: "day_of_week", label: "Day of week ==" },
  { value: "length_of_stay", label: "Length of stay (nights) ≥" },
  { value: "min_price", label: "Min price (floor)" },
  { value: "max_price", label: "Max price (ceiling)" },
];

function kindLabel(kind: string): string {
  return RULE_KINDS.find((k) => k.value === kind)?.label ?? kind;
}

function isClamp(kind: string): boolean {
  return kind === "min_price" || kind === "max_price";
}

function formatBps(bps: number): string {
  const pct = bps / 100;
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(2)}%`;
}

/** Build a UserMessageModel from a caught exception (network/parse failure). */
function errModel(err: unknown): UserMessageModel {
  return {
    what: "The request could not be completed",
    why: err instanceof Error ? err.message : "The request failed.",
    next: "Check your connection and try again.",
  };
}

/** Today as YYYY-MM-DD (client-only; call inside effect/handler). */
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Add `days` to a YYYY-MM-DD string, returning YYYY-MM-DD. */
function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Short weekday + day-of-month label for a header cell. */
function dayHeader(iso: string): { readonly dow: string; readonly dom: string; readonly weekend: boolean } {
  const d = new Date(`${iso}T00:00:00Z`);
  const dowNum = d.getUTCDay();
  const dow = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][dowNum] ?? "";
  return { dow, dom: String(d.getUTCDate()), weekend: dowNum === 0 || dowNum === 6 };
}

// ===========================================================================
// Tab: Overview
// ===========================================================================

function OverviewTab({
  propertyId,
  onError,
}: {
  readonly propertyId: string;
  readonly onError: (m: UserMessageModel | null) => void;
}): ReactElement {
  const [data, setData] = useState<RevenueOverview | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    apiRevenueOverview(propertyId || null)
      .then((res) => {
        if (!active) return;
        const e = asError(res);
        if (e) { onError(toUserMessage(e)); setData(null); }
        else { onError(null); setData(res as RevenueOverview); }
      })
      .catch((err) => { if (active) onError(errModel(err)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [propertyId, onError]);

  if (loading && data === null) {
    return <p className="rev-muted">Loading overview…</p>;
  }
  if (data === null) return <p className="rev-muted">No data.</p>;

  const k = data.kpis;
  return (
    <div>
      <div className="rev-kpis">
        <div className="console-card console-card--gold">
          <p className="console-card__label">Gross revenue (30d)</p>
          <p className="console-card__value">{formatMoney(Number(k.grossRevenueMinor), k.currency)}</p>
        </div>
        <div className="console-card">
          <p className="console-card__label">Avg occupancy</p>
          <p className="console-card__value">{(k.avgOccupancy * 100).toFixed(1)}%</p>
        </div>
        <div className="console-card">
          <p className="console-card__label">Units</p>
          <p className="console-card__value">{k.unitCount}</p>
        </div>
        <div className="console-card">
          <p className="console-card__label">Properties</p>
          <p className="console-card__value">{k.propertyCount}</p>
        </div>
      </div>

      <div className="rev-panel">
        <h3 className="rev-panel__title">Property performance</h3>
        <div className="rev-tablewrap">
          <table className="rev-table">
            <thead>
              <tr>
                <th scope="col">Property</th>
                <th scope="col">City</th>
                <th scope="col">Units</th>
                <th scope="col">Revenue (30d)</th>
                <th scope="col">Occupancy</th>
              </tr>
            </thead>
            <tbody>
              {data.properties.length === 0 ? (
                <tr><td colSpan={5} className="rev-muted">No properties.</td></tr>
              ) : (
                data.properties.map((p) => (
                  <tr key={p.propertyId}>
                    <td>{p.propertyName}</td>
                    <td>{p.city ?? "—"}</td>
                    <td>{p.unitCount}</td>
                    <td>{formatMoney(Number(p.revenueMinor), p.currency)}</td>
                    <td>{(p.occupancy * 100).toFixed(1)}%</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Tab: Inventory (AVOS is the inventory source of truth; publishes to Channex)
// ===========================================================================

interface InvCellEdit {
  readonly row: InventoryRow;
  readonly cell: InventoryCell;
}

function InventoryTab({
  propertyId,
  onError,
}: {
  readonly propertyId: string;
  readonly onError: (m: UserMessageModel | null) => void;
}): ReactElement {
  const [from, setFrom] = useState<string>("");
  const [days, setDays] = useState(14);
  const [cal, setCal] = useState<InventoryCalendar | null>(null);
  const [summary, setSummary] = useState<{ physical: number; booked: number; blocked: number; available: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState<InvCellEdit | null>(null);
  const [blockOpen, setBlockOpen] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const [roomFilter, setRoomFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<"all" | "available" | "low" | "sold_out" | "closed" | "blocked">("all");
  const [flash, setFlash] = useState<string | null>(null);

  useEffect(() => { setFrom(todayIso()); }, []);
  const to = useMemo(() => (from ? addDaysIso(from, Math.max(1, days) - 1) : ""), [from, days]);

  const load = useCallback(async () => {
    if (from === "" || to === "") return;
    setLoading(true);
    try {
      const [c, sm] = await Promise.all([
        apiInventoryCalendar({ propertyId: propertyId || null, from, to }),
        apiInventorySummary(propertyId || null),
      ]);
      const e = asError(c);
      if (e) { onError(toUserMessage(e)); setCal(null); }
      else { onError(null); setCal(c as InventoryCalendar); }
      if (!asError(sm)) setSummary(sm as { physical: number; booked: number; blocked: number; available: number });
    } catch (err) { onError(errModel(err)); }
    finally { setLoading(false); }
  }, [propertyId, from, to, onError]);

  useEffect(() => { void load(); }, [load]);

  function cellMatches(c: InventoryCell): boolean {
    if (statusFilter === "all") return true;
    if (statusFilter === "closed") return c.closed;
    if (statusFilter === "blocked") return c.blocked > 0;
    if (statusFilter === "sold_out") return !c.closed && c.sellable === 0;
    if (statusFilter === "low") return !c.closed && c.sellable > 0 && c.sellable <= Math.max(1, Math.floor(c.physicalRooms * 0.3));
    if (statusFilter === "available") return !c.closed && c.sellable > 0;
    return true;
  }

  const rows = (cal?.rows ?? []).filter((r) => roomFilter === "" || r.unitId === roomFilter);

  function cellClass(c: InventoryCell): string {
    if (c.closed) return "rev-cell rev-cell--stop";
    if (c.sellable === 0) return "rev-cell rev-cell--stop";
    if (c.sellable <= Math.max(1, Math.floor(c.physicalRooms * 0.3))) return "rev-cell rev-cell--override";
    return "rev-cell";
  }

  return (
    <div>
      <p className="rev-help">Manage room inventory in AVOS. Changes publish automatically to the Channel Manager and out to connected channels. No manual sync needed.</p>

      {summary && (
        <div className="rev-synchealth" style={{ marginBottom: "1rem" }}>
          <div className="rev-synchealth__item"><span className="rev-muted">Physical rooms</span> <strong>{summary.physical}</strong></div>
          <div className="rev-synchealth__item"><span className="rev-muted">Available today</span> <strong>{summary.available}</strong></div>
          <div className="rev-synchealth__item"><span className="rev-muted">Booked today</span> <strong>{summary.booked}</strong></div>
          <div className="rev-synchealth__item"><span className="rev-muted">Blocked / out of order</span> <strong>{summary.blocked}</strong></div>
        </div>
      )}

      <div className="rev-toolbar">
        <div className="console-field rev-inline">
          <label htmlFor="inv-from">From</label>
          <input id="inv-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="console-field rev-inline">
          <label htmlFor="inv-days">Days</label>
          <select id="inv-days" value={String(days)} onChange={(e) => setDays(Number(e.target.value))}>
            <option value="14">14</option><option value="30">30</option><option value="60">60</option>
          </select>
        </div>
        <div className="console-field rev-inline">
          <label htmlFor="inv-room">Room type</label>
          <select id="inv-room" value={roomFilter} onChange={(e) => setRoomFilter(e.target.value)}>
            <option value="">All</option>
            {(cal?.rows ?? []).map((r) => <option key={r.unitId} value={r.unitId}>{r.unitName}</option>)}
          </select>
        </div>
        <div className="console-field rev-inline">
          <label htmlFor="inv-status">Availability</label>
          <select id="inv-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}>
            <option value="all">All</option><option value="available">Available</option><option value="low">Low availability</option>
            <option value="sold_out">Sold out</option><option value="closed">Closed</option><option value="blocked">Blocked</option>
          </select>
        </div>
        <button type="button" className="console-btn--ghost" onClick={() => setBlockOpen(true)}>Block rooms</button>
        <button type="button" className="console-btn--ghost" onClick={() => setBatchOpen(true)}>Bulk update</button>
        {flash !== null && <span className="rev-flash" role="status">{flash}</span>}
      </div>

      {loading && cal === null ? (
        <p className="rev-muted">Loading inventory…</p>
      ) : cal === null || rows.length === 0 ? (
        <p className="rev-muted">No room types to manage for this selection.</p>
      ) : (
        <div className="rev-tablewrap">
          <table className="rev-calendar">
            <thead>
              <tr>
                <th scope="col" className="rev-calendar__unitcol">Room</th>
                {cal.dates.map((d) => { const h = dayHeader(d); return (
                  <th key={d} scope="col" className={h.weekend ? "rev-calendar__day rev-calendar__day--weekend" : "rev-calendar__day"}>
                    <span className="rev-calendar__dow">{h.dow}</span><span className="rev-calendar__dom">{h.dom}</span>
                  </th>); })}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.unitId}>
                  <th scope="row" className="rev-calendar__unitcol">
                    <span className="rev-calendar__unitname">{row.unitName}</span>
                    <span className="rev-calendar__rooms">{row.physicalRooms} rooms</span>
                  </th>
                  {row.cells.map((c) => (
                    <td key={c.day} className="rev-calendar__cellwrap">
                      <button type="button" className={cellClass(c) + (cellMatches(c) ? "" : " rev-cell--dim")}
                        onClick={() => setEdit({ row, cell: c })}
                        title={`${row.unitName} · ${c.day}`}>
                        {c.closed ? <span className="rev-cell__ss">Closed</span> : <span className="rev-cell__rate">{c.sellable}</span>}
                      </button>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {edit !== null && (
        <InventoryCellDrawer
          propertyId={propertyId}
          edit={edit}
          onClose={() => setEdit(null)}
          onError={onError}
          onDone={(msg) => { setFlash(msg); setEdit(null); void load(); }}
        />
      )}
      {blockOpen && (
        <BlockRoomsModal
          propertyId={propertyId}
          rooms={(cal?.rows ?? []).map((r) => ({ unitId: r.unitId, unitName: r.unitName, physicalRooms: r.physicalRooms }))}
          onClose={() => setBlockOpen(false)}
          onError={onError}
          onDone={(msg) => { setFlash(msg); setBlockOpen(false); void load(); }}
        />
      )}
      {batchOpen && (
        <InventoryBatchModal
          propertyId={propertyId}
          rooms={(cal?.rows ?? []).map((r) => ({ unitId: r.unitId, unitName: r.unitName, physicalRooms: r.physicalRooms }))}
          onClose={() => setBatchOpen(false)}
          onError={onError}
          onDone={(msg) => { setFlash(msg); setBatchOpen(false); void load(); }}
        />
      )}
    </div>
  );
}

function InventoryCellDrawer({ propertyId, edit, onClose, onError, onDone }: {
  readonly propertyId: string; readonly edit: InvCellEdit;
  readonly onClose: () => void; readonly onError: (m: UserMessageModel | null) => void; readonly onDone: (msg: string) => void;
}): ReactElement {
  const { row, cell } = edit;
  const [available, setAvailableRooms] = useState<string>(String(cell.sellable));
  const [busy, setBusy] = useState(false);

  async function save(): Promise<void> {
    const n = Number(available);
    if (!Number.isFinite(n) || n < 0 || n > row.physicalRooms) {
      onError({ what: "Invalid available rooms", why: `Enter a value between 0 and ${row.physicalRooms}.`, next: "Adjust the number and try again." });
      return;
    }
    setBusy(true);
    try {
      const res = await apiInventorySetAvailable({ propertyId, unitId: row.unitId, day: cell.day, availableRooms: n });
      const e = asError(res); if (e) { onError(toUserMessage(e)); return; }
      onDone("Inventory saved · syncing to Channel Manager");
    } catch (err) { onError(errModel(err)); } finally { setBusy(false); }
  }
  async function doClose(close: boolean): Promise<void> {
    setBusy(true);
    try {
      const res = close
        ? await apiInventorySetAvailable({ propertyId, unitId: row.unitId, day: cell.day, close: true })
        : await apiInventoryReopen({ propertyId, unitId: row.unitId, day: cell.day });
      const e = asError(res); if (e) { onError(toUserMessage(e)); return; }
      onDone(close ? "Closed · syncing" : "Reopened · syncing");
    } catch (err) { onError(errModel(err)); } finally { setBusy(false); }
  }

  return (
    <div className="rev-modal" role="dialog" aria-modal="true" aria-label="Inventory details">
      <div className="rev-modal__backdrop" onClick={onClose} />
      <div className="rev-modal__panel">
        <h3 className="rev-modal__title">{row.unitName} · {cell.day}</h3>
        <dl className="rev-recon-summary">
          <div><dt>Physical rooms</dt><dd>{cell.physicalRooms}</dd></div>
          <div><dt>Booked</dt><dd>{cell.booked}</dd></div>
          <div><dt>Blocked</dt><dd>{cell.blocked}</dd></div>
          <div><dt>Sellable</dt><dd>{cell.closed ? "Closed" : cell.sellable}</dd></div>
          <div><dt>Channel Manager</dt><dd>{cell.closed ? "Closed" : cell.sellable}</dd></div>
          <div><dt>Sync</dt><dd>Auto</dd></div>
        </dl>
        <div className="console-field">
          <label htmlFor="inv-avail">Available rooms (0–{cell.physicalRooms})</label>
          <input id="inv-avail" type="number" min="0" max={cell.physicalRooms} value={available} disabled={cell.closed} onChange={(e) => setAvailableRooms(e.target.value)} />
        </div>
        <p className="rev-help">Available rooms is the sellable count AVOS publishes. It cannot exceed physical rooms minus booked and blocked.</p>
        <div className="rev-modal__actions">
          {cell.closed
            ? <button type="button" className="console-btn--ghost" onClick={() => void doClose(false)} disabled={busy}>Reopen</button>
            : <button type="button" className="console-btn--ghost" onClick={() => void doClose(true)} disabled={busy}>Close inventory</button>}
          <button type="button" className="console-btn--ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="console-btn" onClick={() => void save()} disabled={busy || cell.closed}>{busy ? "Saving…" : "Save & sync"}</button>
        </div>
      </div>
    </div>
  );
}

const BLOCK_REASONS: readonly { value: string; label: string }[] = [
  { value: "maintenance", label: "Maintenance" },
  { value: "owner_block", label: "Owner Block" },
  { value: "renovation", label: "Renovation" },
  { value: "out_of_order", label: "Out of Order" },
  { value: "private_use", label: "Private Use" },
  { value: "other", label: "Other" },
];

function BlockRoomsModal({ propertyId, rooms, onClose, onError, onDone }: {
  readonly propertyId: string; readonly rooms: readonly { unitId: string; unitName: string; physicalRooms: number }[];
  readonly onClose: () => void; readonly onError: (m: UserMessageModel | null) => void; readonly onDone: (msg: string) => void;
}): ReactElement {
  const [unitId, setUnitId] = useState(rooms[0]?.unitId ?? "");
  const [start, setStart] = useState(todayIso());
  const [end, setEnd] = useState(addDaysIso(todayIso(), 1));
  const [quantity, setQuantity] = useState("1");
  const [reason, setReason] = useState("maintenance");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const cap = rooms.find((r) => r.unitId === unitId)?.physicalRooms ?? 1;

  async function submit(): Promise<void> {
    const q = Number(quantity);
    if (!Number.isFinite(q) || q < 1 || q > cap) { onError({ what: "Invalid quantity", why: `Block between 1 and ${cap} rooms.`, next: "Adjust the quantity." }); return; }
    if (end <= start) { onError({ what: "Invalid dates", why: "End date must be after start date.", next: "Adjust the dates." }); return; }
    setBusy(true);
    try {
      const res = await apiCreateInventoryBlock({ propertyId, unitId, startDate: start, endDate: end, quantity: q, reason, notes: notes || null });
      const e = asError(res); if (e) { onError(toUserMessage(e)); return; }
      onDone("Rooms blocked · syncing to Channel Manager");
    } catch (err) { onError(errModel(err)); } finally { setBusy(false); }
  }

  return (
    <div className="rev-modal" role="dialog" aria-modal="true" aria-label="Block rooms">
      <div className="rev-modal__backdrop" onClick={onClose} />
      <div className="rev-modal__panel">
        <h3 className="rev-modal__title">Block rooms</h3>
        <div className="console-field">
          <label htmlFor="blk-room">Room type</label>
          <select id="blk-room" value={unitId} onChange={(e) => setUnitId(e.target.value)}>
            {rooms.map((r) => <option key={r.unitId} value={r.unitId}>{r.unitName} ({r.physicalRooms} rooms)</option>)}
          </select>
        </div>
        <div className="console-form__row">
          <div className="console-field"><label htmlFor="blk-start">Start</label><input id="blk-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} /></div>
          <div className="console-field"><label htmlFor="blk-end">End</label><input id="blk-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
          <div className="console-field"><label htmlFor="blk-qty">Rooms ({"1"}–{cap})</label><input id="blk-qty" type="number" min="1" max={cap} value={quantity} onChange={(e) => setQuantity(e.target.value)} /></div>
        </div>
        <div className="console-field">
          <label htmlFor="blk-reason">Reason</label>
          <select id="blk-reason" value={reason} onChange={(e) => setReason(e.target.value)}>
            {BLOCK_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
        <div className="console-field"><label htmlFor="blk-notes">Notes (optional)</label><input id="blk-notes" type="text" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
        <p className="rev-help">Dates are start-inclusive, end-exclusive (the end date itself stays sellable).</p>
        <div className="rev-modal__actions">
          <button type="button" className="console-btn--ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="console-btn" onClick={() => void submit()} disabled={busy}>{busy ? "Blocking…" : "Block & sync"}</button>
        </div>
      </div>
    </div>
  );
}

interface InvBatchState { unitId: string; action: InventoryBatchLine["action"]; from: string; to: string; availableRooms: string; quantity: string; reason: string; }
function emptyInvLine(unitId: string): InvBatchState { return { unitId, action: "set", from: todayIso(), to: addDaysIso(todayIso(), 6), availableRooms: "", quantity: "1", reason: "maintenance" }; }

function InventoryBatchModal({ propertyId, rooms, onClose, onError, onDone }: {
  readonly propertyId: string; readonly rooms: readonly { unitId: string; unitName: string; physicalRooms: number }[];
  readonly onClose: () => void; readonly onError: (m: UserMessageModel | null) => void; readonly onDone: (msg: string) => void;
}): ReactElement {
  const [lines, setLines] = useState<InvBatchState[]>([emptyInvLine(rooms[0]?.unitId ?? "")]);
  const [preview, setPreview] = useState<{ changes: readonly { room: string; day: string; oldAvailable: number; newAvailable: number }[]; lineCount: number; roomsAffected: number; datesAffected: number } | null>(null);
  const [busy, setBusy] = useState(false);
  function upd(i: number, patch: Partial<InvBatchState>): void { setLines((p) => p.map((l, idx) => idx === i ? { ...l, ...patch } : l)); setPreview(null); }
  function add(): void { setLines((p) => [...p, emptyInvLine(p[p.length - 1]?.unitId ?? rooms[0]?.unitId ?? "")]); setPreview(null); }
  function dup(i: number): void { setLines((p) => { const n = [...p]; n.splice(i + 1, 0, { ...p[i]! }); return n; }); setPreview(null); }
  function rm(i: number): void { setLines((p) => p.length <= 1 ? p : p.filter((_l, idx) => idx !== i)); setPreview(null); }

  function payload(): InventoryBatchLine[] {
    return lines.map((l) => ({ unitId: l.unitId, action: l.action, from: l.from, to: l.to,
      ...(l.action === "set" && l.availableRooms.trim() !== "" ? { availableRooms: Number(l.availableRooms) } : {}),
      ...(l.action === "block" ? { quantity: Number(l.quantity), reason: l.reason } : {}) }));
  }
  async function run(isPreview: boolean): Promise<void> {
    setBusy(true);
    try {
      const res = await apiInventoryBatch({ propertyId, updates: payload(), preview: isPreview });
      const e = asError(res); if (e) { onError(toUserMessage(e)); return; }
      onError(null);
      if (isPreview && "preview" in res) setPreview(res as never);
      else if ("applied" in res) { setPreview(null); onDone(`Inventory batch applied · ${res.lineCount} update(s) · ${res.availabilityJobs} sync job(s)`); }
      else {
        onError({ what: "The inventory update did not complete.", why: "The server returned an unexpected response.", next: "Try again. If this persists, contact support." });
      }
    } catch (err) { onError(errModel(err)); } finally { setBusy(false); }
  }

  return (
    <div className="rev-modal" role="dialog" aria-modal="true" aria-label="Bulk inventory update">
      <div className="rev-modal__backdrop" onClick={busy ? undefined : onClose} />
      <div className="rev-modal__panel" style={{ width: "min(42rem, calc(100vw - 2rem))" }}>
        <h3 className="rev-modal__title">Bulk inventory update</h3>
        {lines.map((l, i) => (
          <div key={i} className="rev-batchline">
            <div className="rev-batchline__head"><strong>Update {i + 1}</strong>
              <div className="rev-batchline__lineactions">
                <button type="button" className="rev-link" onClick={() => dup(i)}>Duplicate</button>
                <button type="button" className="rev-link" onClick={() => rm(i)} disabled={lines.length <= 1}>Remove</button>
              </div>
            </div>
            <div className="console-form__row">
              <div className="console-field"><label htmlFor={`ib-room-${i}`}>Room</label>
                <select id={`ib-room-${i}`} value={l.unitId} onChange={(e) => upd(i, { unitId: e.target.value })}>{rooms.map((r) => <option key={r.unitId} value={r.unitId}>{r.unitName}</option>)}</select></div>
              <div className="console-field"><label htmlFor={`ib-act-${i}`}>Action</label>
                <select id={`ib-act-${i}`} value={l.action} onChange={(e) => upd(i, { action: e.target.value as InventoryBatchLine["action"] })}>
                  <option value="set">Set available rooms</option><option value="block">Block rooms</option><option value="release">Release block</option><option value="close">Close inventory</option><option value="reopen">Reopen inventory</option></select></div>
              <div className="console-field"><label htmlFor={`ib-from-${i}`}>From</label><input id={`ib-from-${i}`} type="date" value={l.from} onChange={(e) => upd(i, { from: e.target.value })} /></div>
              <div className="console-field"><label htmlFor={`ib-to-${i}`}>To</label><input id={`ib-to-${i}`} type="date" value={l.to} onChange={(e) => upd(i, { to: e.target.value })} /></div>
            </div>
            {l.action === "set" && <div className="console-field"><label htmlFor={`ib-avail-${i}`}>Available rooms</label><input id={`ib-avail-${i}`} type="number" min="0" value={l.availableRooms} onChange={(e) => upd(i, { availableRooms: e.target.value })} /></div>}
            {l.action === "block" && <div className="console-form__row">
              <div className="console-field"><label htmlFor={`ib-qty-${i}`}>Rooms to block</label><input id={`ib-qty-${i}`} type="number" min="1" value={l.quantity} onChange={(e) => upd(i, { quantity: e.target.value })} /></div>
              <div className="console-field"><label htmlFor={`ib-rsn-${i}`}>Reason</label><select id={`ib-rsn-${i}`} value={l.reason} onChange={(e) => upd(i, { reason: e.target.value })}>{BLOCK_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}</select></div>
            </div>}
          </div>
        ))}
        <div className="rev-batch__add"><button type="button" className="console-btn--ghost" onClick={add}>+ Add another update</button></div>
        <div className="rev-modal__actions rev-left">
          <button type="button" className="console-btn--ghost" onClick={() => void run(true)} disabled={busy}>Preview changes</button>
          <button type="button" className="console-btn" onClick={() => void run(false)} disabled={busy}>{busy ? "Applying…" : "Apply & sync"}</button>
        </div>
        {preview && (
          <div className="rev-tablewrap rev-preview">
            <p className="rev-muted">{preview.lineCount} update(s) · {preview.roomsAffected} room(s) · {preview.datesAffected} date(s)</p>
            <table className="rev-table"><thead><tr><th scope="col">Room</th><th scope="col">Date</th><th scope="col">Availability</th></tr></thead>
              <tbody>{preview.changes.map((c, idx) => <tr key={idx}><td>{c.room}</td><td>{c.day}</td><td>{c.oldAvailable} → <span className="rev-preview__new">{c.newAvailable}</span></td></tr>)}</tbody></table>
          </div>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// Tab: Rate Calendar (the core screen)
// ===========================================================================

interface CellEdit {
  readonly row: CalendarRow;
  /** The specific rate plan being edited (one room + one rate plan + one date). */
  readonly ratePlanId: string;
  readonly ratePlanName: string;
  readonly minStay: number;
  readonly maxStay: number | null;
  readonly closedToArrival: boolean;
  readonly closedToDeparture: boolean;
  readonly day: string;
  readonly currentRateMinor: string;
  readonly stopSell: boolean;
}

function RateCalendarTab({
  propertyId,
  onError,
}: {
  readonly propertyId: string;
  readonly onError: (m: UserMessageModel | null) => void;
}): ReactElement {
  const [from, setFrom] = useState<string>("");
  const [days, setDays] = useState(14);
  const [cal, setCal] = useState<RateCalendar | null>(null);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState<CellEdit | null>(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  // Seed `from` on the client only (SSR-safe).
  useEffect(() => { setFrom(todayIso()); }, []);

  const to = useMemo(() => (from ? addDaysIso(from, Math.max(1, days) - 1) : ""), [from, days]);

  const load = useCallback(async () => {
    if (from === "" || to === "") return;
    setLoading(true);
    try {
      const res = await apiRateCalendar({ propertyId: propertyId || null, from, to });
      const e = asError(res);
      if (e) { onError(toUserMessage(e)); setCal(null); }
      else { onError(null); setCal(res as RateCalendar); }
    } catch (err) { onError(errModel(err)); }
    finally { setLoading(false); }
  }, [propertyId, from, to, onError]);

  useEffect(() => { void load(); }, [load]);

  async function saveCell(payload: {
    rateChanged: boolean; rateMajor: string;
    stopSellChanged: boolean; stopSell: boolean;
    minStayChanged: boolean; minStay: string; minStayType: "arrival" | "through";
    maxStayChanged: boolean; maxStay: string;
    ctaChanged: boolean; closedToArrival: boolean;
    ctdChanged: boolean; closedToDeparture: boolean;
  }): Promise<void> {
    if (edit === null) return;
    const minor = payload.rateMajor.trim() === "" ? null : parseMoneyToMinor(payload.rateMajor);
    setBusy(true);
    try {
      // DELTA SYNC (certification §4 / Test #2): send ONLY the fields the
      // operator changed. Omitted fields are left untouched both in AVOS and in
      // the Channex delta — so a rate-only edit's payload contains ONLY the
      // rate (+ identifiers/date), never stop_sell / CTA / CTD.
      const res = await apiSaveCalendarCell({
        unitId: edit.row.unitId,
        day: edit.day,
        // One room + one rate plan + one date (Channex Test #2).
        ratePlanId: edit.ratePlanId,
        ...(payload.rateChanged ? { rateMinor: minor !== null ? String(minor) : null } : {}),
        // Stop-sell drives availability open/closed: only send it (and the
        // paired isAvailable + explicit stopSellToggle marker) when it changed.
        ...(payload.stopSellChanged
          ? { isAvailable: !payload.stopSell, stopSell: payload.stopSell, stopSellToggle: true }
          : {}),
        ...(payload.minStayChanged
          ? { minStay: payload.minStay.trim() !== "" ? Number(payload.minStay) : null, minStayType: payload.minStayType }
          : {}),
        ...(payload.maxStayChanged
          ? { maxStay: payload.maxStay.trim() !== "" ? Number(payload.maxStay) : null }
          : {}),
        ...(payload.ctaChanged ? { closedToArrival: payload.closedToArrival } : {}),
        ...(payload.ctdChanged ? { closedToDeparture: payload.closedToDeparture } : {}),
      });
      const e = asError(res);
      if (e) { onError(toUserMessage(e)); }
      else {
        onError(null);
        // Durable outbox: the change is saved + queued for automatic Channel
        // Manager sync (no manual Sync). Show a "Syncing" state.
        setFlash("Saved · syncing to Channel Manager");
        setEdit(null);
        await load();
      }
    } catch (err) { onError(errModel(err)); }
    finally { setBusy(false); }
  }

  return (
    <div>
      <div className="rev-toolbar">
        <div className="console-field rev-inline">
          <label htmlFor="rev-cal-from">From</label>
          <input id="rev-cal-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="console-field rev-inline">
          <label htmlFor="rev-cal-days">Days</label>
          <select id="rev-cal-days" value={String(days)} onChange={(e) => setDays(Number(e.target.value))}>
            <option value="7">7</option>
            <option value="14">14</option>
            <option value="30">30</option>
          </select>
        </div>
        {flash !== null && <span className="rev-flash" role="status">{flash}</span>}
      </div>

      {loading && cal === null ? (
        <p className="rev-muted">Loading calendar…</p>
      ) : cal === null || cal.rows.length === 0 ? (
        <p className="rev-muted">No units to price for this selection.</p>
      ) : (
        <div className="rev-tablewrap">
          <table className="rev-calendar">
            <thead>
              <tr>
                <th scope="col" className="rev-calendar__unitcol">Room</th>
                {cal.dates.map((d) => {
                  const h = dayHeader(d);
                  return (
                    <th key={d} scope="col" className={h.weekend ? "rev-calendar__day rev-calendar__day--weekend" : "rev-calendar__day"}>
                      <span className="rev-calendar__dow">{h.dow}</span>
                      <span className="rev-calendar__dom">{h.dom}</span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {cal.rows.map((row) => {
                // Room → Rate Plan hierarchy. Each rate plan renders its OWN
                // daily rate (never collapsed into one room-level rate). Falls
                // back to the single room row if a server omits ratePlans.
                const planRows = (row.ratePlans && row.ratePlans.length > 0)
                  ? row.ratePlans
                  : [{
                      ratePlanId: row.ratePlanId ?? "",
                      ratePlanName: "Rate",
                      baseRateMinor: row.baseRateMinor,
                      minStay: row.minStay,
                      maxStay: row.maxStay,
                      minStayType: "arrival" as const,
                      closedToArrival: row.closedToArrival,
                      closedToDeparture: row.closedToDeparture,
                      cells: row.cells,
                    }];
                return (
                  <>
                    <tr key={row.unitId} className="rev-calendar__roomrow">
                      <th scope="row" className="rev-calendar__unitcol rev-calendar__roomhdr" colSpan={1}>
                        <span className="rev-calendar__unitname">{row.unitName}</span>
                        {typeof row.countOfRooms === "number" && (
                          <span className="rev-calendar__rooms" title="Physical room count">{row.countOfRooms} rooms</span>
                        )}
                      </th>
                      {cal.dates.map((d) => <td key={d} className="rev-calendar__roomspacer" aria-hidden="true" />)}
                    </tr>
                    {planRows.map((rp) => (
                      <tr key={row.unitId + "|" + rp.ratePlanId}>
                        <th scope="row" className="rev-calendar__unitcol rev-calendar__planhdr">
                          <span className="rev-calendar__planname">{rp.ratePlanName}</span>
                          <span className="rev-calendar__restr">
                            {rp.minStay > 1 && <span className="rev-chip" title={`Minimum stay ${rp.minStay} nights`}>M{rp.minStay}</span>}
                            {rp.maxStay !== null && <span className="rev-chip" title={`Maximum stay ${rp.maxStay} nights`}>MAX{rp.maxStay}</span>}
                            {rp.closedToArrival && <span className="rev-chip" title="Closed to arrival">CTA</span>}
                            {rp.closedToDeparture && <span className="rev-chip" title="Closed to departure">CTD</span>}
                          </span>
                        </th>
                        {rp.cells.map((c) => (
                          <td key={c.day} className="rev-calendar__cellwrap">
                            <button
                              type="button"
                              className={
                                "rev-cell" +
                                (c.stopSell ? " rev-cell--stop" : "") +
                                (c.overridden ? " rev-cell--override" : "") +
                                (c.hasRestriction ? " rev-cell--restr" : "")
                              }
                              onClick={() => setEdit({
                                row,
                                ratePlanId: rp.ratePlanId,
                                ratePlanName: rp.ratePlanName,
                                minStay: c.minStay ?? rp.minStay,
                                maxStay: c.maxStay ?? rp.maxStay,
                                closedToArrival: c.closedToArrival ?? rp.closedToArrival,
                                closedToDeparture: c.closedToDeparture ?? rp.closedToDeparture,
                                day: c.day,
                                currentRateMinor: c.rateMinor,
                                stopSell: c.stopSell,
                              })}
                              title={`${row.unitName} · ${rp.ratePlanName} · ${c.day}`}
                            >
                              {c.stopSell ? (
                                <span className="rev-cell__ss">SS</span>
                              ) : (
                                <span className="rev-cell__rate">{formatMoney(Number(c.rateMinor), row.currency)}</span>
                              )}
                            </button>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {edit !== null && (
        <CellEditPopover
          edit={edit}
          busy={busy}
          onClose={() => setEdit(null)}
          onSave={saveCell}
        />
      )}

      {cal !== null && cal.rows.length > 0 && (
        <BatchComposerPanel rows={cal.rows} onDone={(msg) => { setFlash(msg); void load(); }} onError={onError} />
      )}
    </div>
  );
}

function CellEditPopover({
  edit,
  busy,
  onClose,
  onSave,
}: {
  readonly edit: CellEdit;
  readonly busy: boolean;
  readonly onClose: () => void;
  readonly onSave: (payload: {
    rateChanged: boolean; rateMajor: string;
    stopSellChanged: boolean; stopSell: boolean;
    minStayChanged: boolean; minStay: string; minStayType: "arrival" | "through";
    maxStayChanged: boolean; maxStay: string;
    ctaChanged: boolean; closedToArrival: boolean;
    ctdChanged: boolean; closedToDeparture: boolean;
  }) => void;
}): ReactElement {
  const [rate, setRate] = useState<string>(() => (Number(edit.currentRateMinor) / 100).toFixed(2));
  const [stopSell, setStopSell] = useState<boolean>(edit.stopSell);
  const [minStay, setMinStay] = useState<string>(edit.minStay > 1 ? String(edit.minStay) : "");
  const [minStayType, setMinStayType] = useState<"arrival" | "through">("arrival");
  const [maxStay, setMaxStay] = useState<string>(edit.maxStay !== null ? String(edit.maxStay) : "");
  const [cta, setCta] = useState<boolean>(edit.closedToArrival);
  const [ctd, setCtd] = useState<boolean>(edit.closedToDeparture);

  return (
    <div className="rev-modal" role="dialog" aria-modal="true" aria-label="Edit rate & restrictions">
      <div className="rev-modal__backdrop" onClick={onClose} />
      <div className="rev-modal__panel">
        <h3 className="rev-modal__title">{edit.row.unitName} · {edit.ratePlanName} · {edit.day}</h3>
        <div className="console-field">
          <label htmlFor="rev-cell-rate">Rate ({edit.row.currency})</label>
          <input id="rev-cell-rate" type="number" step="0.01" value={rate} disabled={stopSell} onChange={(e) => setRate(e.target.value)} />
        </div>
        <div className="console-form__row">
          <div className="console-field">
            <label htmlFor="rev-cell-min">Minimum stay</label>
            <input id="rev-cell-min" type="number" min="1" value={minStay} placeholder="—" onChange={(e) => setMinStay(e.target.value)} />
          </div>
          <div className="console-field">
            <label htmlFor="rev-cell-mintype">Min stay type</label>
            <select id="rev-cell-mintype" value={minStayType} onChange={(e) => setMinStayType(e.target.value as "arrival" | "through")}>
              <option value="arrival">Arrival</option>
              <option value="through">Through</option>
            </select>
          </div>
          <div className="console-field">
            <label htmlFor="rev-cell-max">Maximum stay</label>
            <input id="rev-cell-max" type="number" min="0" value={maxStay} placeholder="—" onChange={(e) => setMaxStay(e.target.value)} />
          </div>
        </div>
        <p className="rev-help">Arrival: min stay is evaluated from the guest\'s check-in date. Through: it applies across the stay dates.</p>
        <label className="rev-check">
          <input type="checkbox" checked={stopSell} onChange={(e) => setStopSell(e.target.checked)} />
          <span>Stop-sell (distribution closed — physical inventory unchanged)</span>
        </label>
        <label className="rev-check">
          <input type="checkbox" checked={cta} onChange={(e) => setCta(e.target.checked)} />
          <span>Closed to arrival (CTA)</span>
        </label>
        <label className="rev-check">
          <input type="checkbox" checked={ctd} onChange={(e) => setCtd(e.target.checked)} />
          <span>Closed to departure (CTD)</span>
        </label>
        <div className="rev-modal__actions">
          <button type="button" className="console-btn--ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="console-btn"
            onClick={() => {
              // DELTA DISCIPLINE (certification §4 / Test #2): send ONLY the
              // fields the operator actually changed vs the cell's loaded state.
              // A rate-only edit must not carry stop_sell / CTA / CTD, or Channex
              // rejects the single-date single-rate update.
              const origRate = (Number(edit.currentRateMinor) / 100).toFixed(2);
              const origMin = edit.minStay > 1 ? String(edit.minStay) : "";
              const origMax = edit.maxStay !== null ? String(edit.maxStay) : "";
              onSave({
                rateChanged: rate.trim() !== origRate,
                rateMajor: rate,
                stopSellChanged: stopSell !== edit.stopSell,
                stopSell,
                minStayChanged: minStay.trim() !== origMin || (minStay.trim() !== "" && minStayType !== "arrival"),
                minStay, minStayType,
                maxStayChanged: maxStay.trim() !== origMax,
                maxStay,
                ctaChanged: cta !== edit.closedToArrival,
                closedToArrival: cta,
                ctdChanged: ctd !== edit.closedToDeparture,
                closedToDeparture: ctd,
              });
            }}
            disabled={busy}>
            {busy ? "Saving…" : "Save & sync"}
          </button>
        </div>
      </div>
    </div>
  );
}

interface BatchLineState {
  unitId: string;
  ratePlanId: string;
  from: string;
  to: string;
  action: "set" | "none";
  rate: string;
  minStay: string;
  minStayType: "arrival" | "through";
  maxStay: string;
  stopSell: "" | "on" | "off";
  cta: "" | "on" | "off";
  ctd: "" | "on" | "off";
}

function emptyLine(unitId: string, ratePlanId: string): BatchLineState {
  return {
    unitId, ratePlanId,
    from: "", to: "",
    action: "set",
    rate: "",
    minStay: "", minStayType: "arrival",
    maxStay: "",
    stopSell: "", cta: "", ctd: "",
  };
}

/**
 * Batch composer — stage MULTIPLE (room, rate plan, date range, rate/restriction)
 * update lines and submit them in ONE action. Apply & Sync persists all lines in
 * one logical operation and produces ONE combined ARI job → ONE Channex call →
 * ONE task id. A single line still works (does not force multiple rows). Reused
 * for rate + Min/Max stay + Stop Sell + CTA + CTD (Channex Tests 2–8).
 */
function BatchComposerPanel({
  rows,
  onDone,
  onError,
}: {
  readonly rows: readonly CalendarRow[];
  readonly onDone: (message: string) => void;
  readonly onError: (m: UserMessageModel | null) => void;
}): ReactElement {
  // Flatten rooms → their rate plans for the per-line selectors.
  const planOptions = rows.flatMap((r) =>
    (r.ratePlans && r.ratePlans.length > 0 ? r.ratePlans : (r.ratePlanId ? [{ ratePlanId: r.ratePlanId, ratePlanName: "Rate" }] : []))
      .map((rp) => ({ unitId: r.unitId, unitName: r.unitName, ratePlanId: rp.ratePlanId, ratePlanName: rp.ratePlanName })),
  );
  const firstRoom = rows[0]?.unitId ?? "";
  const firstPlan = (rows[0]?.ratePlans?.[0]?.ratePlanId) ?? rows[0]?.ratePlanId ?? "";

  const [lines, setLines] = useState<BatchLineState[]>(() => [emptyLine(firstRoom, firstPlan)]);
  const [preview, setPreview] = useState<(BatchSummary & { preview: true }) | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = todayIso();
    setLines((prev) => prev.map((l) => (l.from === "" ? { ...l, from: t, to: addDaysIso(t, 6) } : l)));
  }, []);

  function plansForRoom(unitId: string) {
    return planOptions.filter((p) => p.unitId === unitId);
  }
  function currencyFor(unitId: string): string {
    return rows.find((r) => r.unitId === unitId)?.currency ?? "INR";
  }
  function update(idx: number, patch: Partial<BatchLineState>): void {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
    setPreview(null);
  }
  function addLine(): void {
    setLines((prev) => {
      const last = prev[prev.length - 1];
      return [...prev, emptyLine(last?.unitId ?? firstRoom, last?.ratePlanId ?? firstPlan)];
    });
    setPreview(null);
  }
  function duplicateLine(idx: number): void {
    setLines((prev) => {
      const copy = { ...prev[idx]! };
      const next = [...prev];
      next.splice(idx + 1, 0, copy);
      return next;
    });
    setPreview(null);
  }
  function removeLine(idx: number): void {
    setLines((prev) => (prev.length <= 1 ? prev : prev.filter((_l, i) => i !== idx)));
    setPreview(null);
  }

  function toPayload() {
    return lines.map((l) => {
      const restr = {
        ...(l.minStay.trim() !== "" ? { minStay: Number(l.minStay), minStayType: l.minStayType } : {}),
        ...(l.maxStay.trim() !== "" ? { maxStay: Number(l.maxStay) } : {}),
        ...(l.stopSell !== "" ? { stopSell: l.stopSell === "on" } : {}),
        ...(l.cta !== "" ? { closedToArrival: l.cta === "on" } : {}),
        ...(l.ctd !== "" ? { closedToDeparture: l.ctd === "on" } : {}),
      };
      return {
        unitId: l.unitId,
        ratePlanId: l.ratePlanId,
        from: l.from,
        to: l.to === "" ? l.from : l.to,
        ...(l.action === "set" && l.rate.trim() !== "" ? { setRateMinor: String(parseMoneyToMinor(l.rate) ?? 0) } : {}),
        ...restr,
      };
    });
  }

  async function run(isPreview: boolean): Promise<void> {
    setBusy(true);
    try {
      const res = await apiBatchUpdateCalendar({ updates: toPayload(), preview: isPreview });
      const e = asError(res);
      if (e) { onError(toUserMessage(e)); return; }
      onError(null);
      if (isPreview && "preview" in res) {
        setPreview(res as BatchSummary & { preview: true });
      } else if ("applied" in res) {
        setPreview(null);
        const jobNote = res.ariJobId ? ` · 1 sync job queued` : "";
        onDone(`Batch applied · ${res.lineCount} update(s)${jobNote} · pushed to Channel Manager`);
      }
    } catch (err) { onError(errModel(err)); }
    finally { setBusy(false); }
  }

  return (
    <div className="rev-panel">
      <h3 className="rev-panel__title">Bulk update</h3>
      <p className="rev-help">Stage one or more updates, then apply them together. All updates are sent to the Channel Manager in a single sync.</p>

      {lines.map((l, idx) => {
        const plans = plansForRoom(l.unitId);
        const cur = currencyFor(l.unitId);
        return (
          <div key={idx} className="rev-batchline">
            <div className="rev-batchline__head">
              <strong>Update {idx + 1}</strong>
              <div className="rev-batchline__lineactions">
                <button type="button" className="rev-link" onClick={() => duplicateLine(idx)}>Duplicate</button>
                <button type="button" className="rev-link" onClick={() => removeLine(idx)} disabled={lines.length <= 1}>Remove</button>
              </div>
            </div>
            <div className="console-form__row">
              <div className="console-field">
                <label htmlFor={`bl-room-${idx}`}>Room</label>
                <select id={`bl-room-${idx}`} value={l.unitId} onChange={(e) => {
                  const unitId = e.target.value;
                  const firstRp = plansForRoom(unitId)[0]?.ratePlanId ?? "";
                  update(idx, { unitId, ratePlanId: firstRp });
                }}>
                  {rows.map((r) => <option key={r.unitId} value={r.unitId}>{r.unitName}</option>)}
                </select>
              </div>
              <div className="console-field">
                <label htmlFor={`bl-plan-${idx}`}>Rate plan</label>
                <select id={`bl-plan-${idx}`} value={l.ratePlanId} onChange={(e) => update(idx, { ratePlanId: e.target.value })}>
                  {plans.map((p) => <option key={p.ratePlanId} value={p.ratePlanId}>{p.ratePlanName}</option>)}
                </select>
              </div>
              <div className="console-field">
                <label htmlFor={`bl-from-${idx}`}>From</label>
                <input id={`bl-from-${idx}`} type="date" value={l.from} onChange={(e) => update(idx, { from: e.target.value })} />
              </div>
              <div className="console-field">
                <label htmlFor={`bl-to-${idx}`}>To</label>
                <input id={`bl-to-${idx}`} type="date" value={l.to} onChange={(e) => update(idx, { to: e.target.value })} />
              </div>
            </div>
            <div className="console-form__row">
              <div className="console-field">
                <label htmlFor={`bl-action-${idx}`}>Action</label>
                <select id={`bl-action-${idx}`} value={l.action} onChange={(e) => update(idx, { action: e.target.value as "set" | "none" })}>
                  <option value="set">Set rate</option>
                  <option value="none">Restrictions only</option>
                </select>
              </div>
              {l.action === "set" && (
                <div className="console-field">
                  <label htmlFor={`bl-rate-${idx}`}>Value ({cur})</label>
                  <input id={`bl-rate-${idx}`} type="number" step="0.01" value={l.rate} onChange={(e) => update(idx, { rate: e.target.value })} />
                </div>
              )}
            </div>
            <details className="rev-restr-details">
              <summary>Restrictions (optional)</summary>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor={`bl-min-${idx}`}>Minimum stay</label>
                  <input id={`bl-min-${idx}`} type="number" min="1" value={l.minStay} placeholder="—" onChange={(e) => update(idx, { minStay: e.target.value })} />
                </div>
                <div className="console-field">
                  <label htmlFor={`bl-mintype-${idx}`}>Min stay type</label>
                  <select id={`bl-mintype-${idx}`} value={l.minStayType} onChange={(e) => update(idx, { minStayType: e.target.value as "arrival" | "through" })}>
                    <option value="arrival">Arrival</option>
                    <option value="through">Through</option>
                  </select>
                </div>
                <div className="console-field">
                  <label htmlFor={`bl-max-${idx}`}>Maximum stay</label>
                  <input id={`bl-max-${idx}`} type="number" min="1" value={l.maxStay} placeholder="—" onChange={(e) => update(idx, { maxStay: e.target.value })} />
                </div>
              </div>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor={`bl-ss-${idx}`}>Stop sell</label>
                  <select id={`bl-ss-${idx}`} value={l.stopSell} onChange={(e) => update(idx, { stopSell: e.target.value as "" | "on" | "off" })}>
                    <option value="">No change</option><option value="on">On</option><option value="off">Off</option>
                  </select>
                </div>
                <div className="console-field">
                  <label htmlFor={`bl-cta-${idx}`}>Closed to arrival</label>
                  <select id={`bl-cta-${idx}`} value={l.cta} onChange={(e) => update(idx, { cta: e.target.value as "" | "on" | "off" })}>
                    <option value="">No change</option><option value="on">On</option><option value="off">Off</option>
                  </select>
                </div>
                <div className="console-field">
                  <label htmlFor={`bl-ctd-${idx}`}>Closed to departure</label>
                  <select id={`bl-ctd-${idx}`} value={l.ctd} onChange={(e) => update(idx, { ctd: e.target.value as "" | "on" | "off" })}>
                    <option value="">No change</option><option value="on">On</option><option value="off">Off</option>
                  </select>
                </div>
              </div>
            </details>
          </div>
        );
      })}

      <div className="rev-batch__add">
        <button type="button" className="console-btn--ghost" onClick={addLine}>+ Add another update</button>
      </div>

      <div className="rev-modal__actions rev-left">
        <button type="button" className="console-btn--ghost" onClick={() => void run(true)} disabled={busy}>Preview changes</button>
        <button type="button" className="console-btn" onClick={() => void run(false)} disabled={busy}>
          {busy ? "Applying…" : "Apply & sync"}
        </button>
      </div>

      {preview !== null && (
        <div className="rev-tablewrap rev-preview">
          <p className="rev-muted">
            {preview.lineCount} update line(s) · {preview.roomsAffected} room(s) · {preview.ratePlansAffected} rate plan(s) · {preview.datesAffected} date(s)
          </p>
          <table className="rev-table">
            <thead><tr><th scope="col">Room</th><th scope="col">Rate plan</th><th scope="col">Dates</th><th scope="col">Change</th></tr></thead>
            <tbody>
              {preview.lines.map((c: BatchLineSummary, i: number) => (
                <tr key={i}>
                  <td>{c.room}</td>
                  <td>{c.ratePlan}</td>
                  <td>{c.from}{c.to !== c.from ? ` → ${c.to}` : ""} ({c.days}d)</td>
                  <td>
                    {c.rateMinor !== undefined && <span className="rev-preview__new">Rate {(Number(c.rateMinor) / 100).toFixed(2)}</span>}
                    {c.minStay !== undefined && <span className="rev-chip">M{c.minStay}{c.minStayType === "through" ? "T" : "A"}</span>}
                    {c.maxStay !== undefined && <span className="rev-chip">MAX{c.maxStay}</span>}
                    {c.stopSell !== undefined && <span className="rev-chip">{c.stopSell ? "StopSell" : "Open"}</span>}
                    {c.closedToArrival !== undefined && <span className="rev-chip">CTA {c.closedToArrival ? "on" : "off"}</span>}
                    {c.closedToDeparture !== undefined && <span className="rev-chip">CTD {c.closedToDeparture ? "on" : "off"}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// Tab: Rate Plans
// ===========================================================================

function RatePlansTab({
  propertyId,
  onError,
}: {
  readonly propertyId: string;
  readonly onError: (m: UserMessageModel | null) => void;
}): ReactElement {
  const [plans, setPlans] = useState<readonly RatePlanRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await apiRevenueListRatePlans(propertyId || null);
      setPlans(rows);
      onError(null);
    } catch (err) { onError(errModel(err)); }
    finally { setLoading(false); }
  }, [propertyId, onError]);

  useEffect(() => { void load(); }, [load]);

  if (loading && plans.length === 0) return <p className="rev-muted">Loading rate plans…</p>;

  return (
    <div className="rev-panel">
      <h3 className="rev-panel__title">Rate plans</h3>
      <div className="rev-tablewrap">
        <table className="rev-table">
          <thead>
            <tr>
              <th scope="col">Room</th>
              <th scope="col">Plan</th>
              <th scope="col">Rate</th>
              <th scope="col">Min stay</th>
              <th scope="col">Max stay</th>
              <th scope="col">Restrictions</th>
            </tr>
          </thead>
          <tbody>
            {plans.length === 0 ? (
              <tr><td colSpan={6} className="rev-muted">No rate plans.</td></tr>
            ) : (
              plans.map((p) => (
                <tr key={p.id}>
                  <td>{p.unitName}</td>
                  <td>{p.name}</td>
                  <td>{formatMoney(Number(p.rateMinor), p.rateCurrency)}</td>
                  <td>{p.minStay}</td>
                  <td>{p.maxStay ?? "—"}</td>
                  <td>
                    {p.closedToArrival && <span className="rev-chip">CTA</span>}
                    {p.closedToDeparture && <span className="rev-chip">CTD</span>}
                    {!p.closedToArrival && !p.closedToDeparture && "—"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===========================================================================
// Tab: Restrictions
// ===========================================================================

function RestrictionsTab({
  propertyId,
  onError,
}: {
  readonly propertyId: string;
  readonly onError: (m: UserMessageModel | null) => void;
}): ReactElement {
  const [plans, setPlans] = useState<readonly RatePlanRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setPlans(await apiRevenueListRatePlans(propertyId || null));
      onError(null);
    } catch (err) { onError(errModel(err)); }
    finally { setLoading(false); }
  }, [propertyId, onError]);

  useEffect(() => { void load(); }, [load]);

  async function save(plan: RatePlanRow, patch: {
    minStay?: number;
    minStayType?: "arrival" | "through";
    maxStay?: number | null;
    stopSell?: boolean;
    closedToArrival?: boolean;
    closedToDeparture?: boolean;
  }): Promise<void> {
    setSavingId(plan.id);
    try {
      const res = await apiSaveRestrictions({ ratePlanId: plan.id, ...patch });
      const e = asError(res);
      if (e) { onError(toUserMessage(e)); }
      else {
        onError(null);
        // Durable outbox: saved + queued for automatic Channel Manager sync.
        setFlash("Saved · syncing to Channel Manager");
        await load();
      }
    } catch (err) { onError(errModel(err)); }
    finally { setSavingId(null); }
  }

  if (loading && plans.length === 0) return <p className="rev-muted">Loading restrictions…</p>;

  return (
    <div className="rev-panel">
      <h3 className="rev-panel__title">Stay restrictions</h3>
      {flash !== null && <p className="rev-flash" role="status">{flash}</p>}
      <div className="rev-tablewrap">
        <table className="rev-table">
          <thead>
            <tr>
              <th scope="col">Room · Plan</th>
              <th scope="col">Min stay</th>
              <th scope="col">Min type</th>
              <th scope="col">Max stay</th>
              <th scope="col">Stop sell</th>
              <th scope="col">CTA</th>
              <th scope="col">CTD</th>
            </tr>
          </thead>
          <tbody>
            {plans.length === 0 ? (
              <tr><td colSpan={7} className="rev-muted">No rate plans.</td></tr>
            ) : (
              plans.map((p) => (
                <RestrictionRow key={p.id} plan={p} saving={savingId === p.id} onSave={save} />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RestrictionRow({
  plan,
  saving,
  onSave,
}: {
  readonly plan: RatePlanRow;
  readonly saving: boolean;
  readonly onSave: (plan: RatePlanRow, patch: { minStay?: number; minStayType?: "arrival" | "through"; maxStay?: number | null; stopSell?: boolean; closedToArrival?: boolean; closedToDeparture?: boolean }) => void;
}): ReactElement {
  const [minStay, setMinStay] = useState(String(plan.minStay));
  const [minStayType, setMinStayType] = useState<"arrival" | "through">((plan.minStayType === "through" ? "through" : "arrival"));
  const [maxStay, setMaxStay] = useState(plan.maxStay === null ? "" : String(plan.maxStay));

  useEffect(() => {
    setMinStay(String(plan.minStay));
    setMinStayType(plan.minStayType === "through" ? "through" : "arrival");
    setMaxStay(plan.maxStay === null ? "" : String(plan.maxStay));
  }, [plan]);

  return (
    <tr>
      <td>{plan.unitName} · {plan.name}</td>
      <td>
        <input className="rev-mini" type="number" min="1" value={minStay} onChange={(e) => setMinStay(e.target.value)}
          onBlur={() => onSave(plan, { minStay: Math.max(1, Number(minStay || "1")) })} disabled={saving} />
      </td>
      <td>
        <select className="rev-mini" value={minStayType} disabled={saving}
          onChange={(e) => { const v = e.target.value as "arrival" | "through"; setMinStayType(v); onSave(plan, { minStayType: v }); }}>
          <option value="arrival">Arrival</option>
          <option value="through">Through</option>
        </select>
      </td>
      <td>
        <input className="rev-mini" type="number" min="0" value={maxStay} placeholder="—" onChange={(e) => setMaxStay(e.target.value)}
          onBlur={() => onSave(plan, { maxStay: maxStay.trim() === "" ? null : Number(maxStay) })} disabled={saving} />
      </td>
      <td>
        <input type="checkbox" checked={plan.stopSell ?? false} onChange={(e) => onSave(plan, { stopSell: e.target.checked })} disabled={saving} aria-label="Stop sell" />
      </td>
      <td>
        <input type="checkbox" checked={plan.closedToArrival} onChange={(e) => onSave(plan, { closedToArrival: e.target.checked })} disabled={saving} aria-label="Closed to arrival" />
      </td>
      <td>
        <input type="checkbox" checked={plan.closedToDeparture} onChange={(e) => onSave(plan, { closedToDeparture: e.target.checked })} disabled={saving} aria-label="Closed to departure" />
      </td>
    </tr>
  );
}

// ===========================================================================
// Tab: Pricing Rules (yield engine + guardrails + simulator)
// ===========================================================================

function PricingRulesTab({
  propertyId,
  properties,
  onError,
}: {
  readonly propertyId: string;
  readonly properties: readonly Property[];
  readonly onError: (m: UserMessageModel | null) => void;
}): ReactElement {
  const [rules, setRules] = useState<readonly YieldRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRules(await apiListYieldRules()); onError(null); }
    catch (err) { onError(errModel(err)); }
    finally { setLoading(false); }
  }, [onError]);

  useEffect(() => { void load(); }, [load]);

  async function createRule(input: {
    name: string; kind: YieldRuleKind; threshold?: number; adjustmentBps?: number;
    floorMinor?: string | null; ceilingMinor?: string | null; priority?: number;
  }): Promise<void> {
    setBusy(true);
    try {
      const res = await apiCreateYieldRule(input);
      const e = asError(res);
      if (e) onError(toUserMessage(e)); else { onError(null); await load(); }
    } catch (err) { onError(errModel(err)); }
    finally { setBusy(false); }
  }

  async function toggle(rule: YieldRule): Promise<void> {
    try {
      const res = await apiSetRuleEnabled({ ruleId: rule.id, enabled: !rule.enabled });
      const e = asError(res);
      if (e) onError(toUserMessage(e)); else { onError(null); await load(); }
    } catch (err) { onError(errModel(err)); }
  }

  async function remove(rule: YieldRule): Promise<void> {
    try {
      const res = await apiDeleteYieldRule(rule.id);
      const e = asError(res);
      if (e) onError(toUserMessage(e)); else { onError(null); await load(); }
    } catch (err) { onError(errModel(err)); }
  }

  return (
    <div>
      {propertyId !== "" && <GuardrailsPanel propertyId={propertyId} onError={onError} />}

      <div className="rev-panel">
        <h3 className="rev-panel__title">Pricing rules</h3>
        {loading && rules.length === 0 ? (
          <p className="rev-muted">Loading rules…</p>
        ) : (
          <div className="rev-tablewrap">
            <table className="rev-table">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Condition</th>
                  <th scope="col">Adjustment</th>
                  <th scope="col">Priority</th>
                  <th scope="col">Enabled</th>
                  <th scope="col"><span className="rev-sr">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {rules.length === 0 ? (
                  <tr><td colSpan={6} className="rev-muted">No rules yet.</td></tr>
                ) : (
                  rules.map((r) => (
                    <tr key={r.id}>
                      <td>{r.name}</td>
                      <td>{kindLabel(r.kind)} {isClamp(r.kind) ? "" : r.threshold}</td>
                      <td>{isClamp(r.kind)
                        ? (r.kind === "min_price" ? `Floor ${r.floorMinor ?? "—"}` : `Ceiling ${r.ceilingMinor ?? "—"}`)
                        : formatBps(r.adjustmentBps)}</td>
                      <td>{r.priority}</td>
                      <td>
                        <button type="button" className={r.enabled ? "rev-toggle rev-toggle--on" : "rev-toggle"} aria-pressed={r.enabled} onClick={() => void toggle(r)}>
                          {r.enabled ? "On" : "Off"}
                        </button>
                      </td>
                      <td><button type="button" className="rev-link-danger" onClick={() => void remove(r)}>Delete</button></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        <NewRuleForm onCreate={createRule} busy={busy} />
      </div>

      <RateSimulator onError={onError} currency={properties.find((p) => p.id === propertyId)?.currency ?? "INR"} />
    </div>
  );
}

function GuardrailsPanel({
  propertyId,
  onError,
}: {
  readonly propertyId: string;
  readonly onError: (m: UserMessageModel | null) => void;
}): ReactElement {
  const [g, setG] = useState<Guardrails | null>(null);
  const [minP, setMinP] = useState("");
  const [maxP, setMaxP] = useState("");
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    apiGetGuardrails(propertyId).then((res) => {
      if (!active) return;
      const e = asError(res);
      if (e) { onError(toUserMessage(e)); return; }
      const gr = res as Guardrails;
      setG(gr);
      setMinP(gr.minPriceMinor === null ? "" : (Number(gr.minPriceMinor) / 100).toFixed(2));
      setMaxP(gr.maxPriceMinor === null ? "" : (Number(gr.maxPriceMinor) / 100).toFixed(2));
    }).catch((err) => onError(errModel(err)));
    return () => { active = false; };
  }, [propertyId, onError]);

  async function save(): Promise<void> {
    setBusy(true);
    try {
      const res = await apiSaveGuardrails({
        propertyId,
        minPriceMinor: minP.trim() === "" ? null : String(parseMoneyToMinor(minP) ?? 0),
        maxPriceMinor: maxP.trim() === "" ? null : String(parseMoneyToMinor(maxP) ?? 0),
      });
      const e = asError(res);
      if (e) onError(toUserMessage(e)); else { onError(null); setFlash("Guardrails saved"); }
    } catch (err) { onError(errModel(err)); }
    finally { setBusy(false); }
  }

  const currency = g?.currency ?? "INR";
  return (
    <div className="rev-panel">
      <h3 className="rev-panel__title">Rate guardrails</h3>
      <p className="rev-muted">The floor and ceiling every dynamic price is clamped to for this property.</p>
      <div className="console-form__row">
        <div className="console-field">
          <label htmlFor="rev-gr-min">Min price ({currency})</label>
          <input id="rev-gr-min" type="number" step="0.01" value={minP} placeholder="none" onChange={(e) => setMinP(e.target.value)} />
        </div>
        <div className="console-field">
          <label htmlFor="rev-gr-max">Max price ({currency})</label>
          <input id="rev-gr-max" type="number" step="0.01" value={maxP} placeholder="none" onChange={(e) => setMaxP(e.target.value)} />
        </div>
      </div>
      <div className="rev-modal__actions rev-left">
        <button type="button" className="console-btn" onClick={() => void save()} disabled={busy}>{busy ? "Saving…" : "Save guardrails"}</button>
        {flash !== null && <span className="rev-flash" role="status">{flash}</span>}
      </div>
    </div>
  );
}

function NewRuleForm({
  onCreate,
  busy,
}: {
  readonly onCreate: (input: {
    name: string; kind: YieldRuleKind; threshold?: number; adjustmentBps?: number;
    floorMinor?: string | null; ceilingMinor?: string | null; priority?: number;
  }) => void;
  readonly busy: boolean;
}): ReactElement {
  const [name, setName] = useState("");
  const [kind, setKind] = useState<YieldRuleKind>("occupancy");
  const [threshold, setThreshold] = useState("0");
  const [adjustmentPct, setAdjustmentPct] = useState("0");
  const [clampAmount, setClampAmount] = useState("");
  const [priority, setPriority] = useState("0");

  const clamp = isClamp(kind);

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const trimmedName = name.trim();
    if (trimmedName === "") return;
    const bps = Math.round(Number(adjustmentPct || "0") * 100);
    const clampMinor = clamp && clampAmount.trim() !== "" ? parseMoneyToMinor(clampAmount) : null;
    onCreate({
      name: trimmedName,
      kind,
      threshold: clamp ? 0 : Number(threshold || "0"),
      adjustmentBps: clamp ? 0 : bps,
      floorMinor: kind === "min_price" && clampMinor !== null ? String(clampMinor) : null,
      ceilingMinor: kind === "max_price" && clampMinor !== null ? String(clampMinor) : null,
      priority: Number(priority || "0"),
    });
    setName(""); setThreshold("0"); setAdjustmentPct("0"); setClampAmount(""); setPriority("0");
  }

  return (
    <form className="console-form" onSubmit={handleSubmit} noValidate>
      <p className="console-form__title">New rule</p>
      <div className="console-form__row">
        <div className="console-field">
          <label htmlFor="rev-new-name">Name</label>
          <input id="rev-new-name" value={name} placeholder="Weekend surge" onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="console-field">
          <label htmlFor="rev-new-kind">Kind</label>
          <select id="rev-new-kind" value={kind} onChange={(e) => setKind(e.target.value as YieldRuleKind)}>
            {RULE_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
          </select>
        </div>
        <div className="console-field">
          <label htmlFor="rev-new-priority">Priority</label>
          <input id="rev-new-priority" type="number" value={priority} onChange={(e) => setPriority(e.target.value)} />
        </div>
      </div>
      <div className="console-form__row">
        {!clamp ? (
          <>
            <div className="console-field">
              <label htmlFor="rev-new-threshold">Threshold</label>
              <input id="rev-new-threshold" type="number" value={threshold} onChange={(e) => setThreshold(e.target.value)} />
            </div>
            <div className="console-field">
              <label htmlFor="rev-new-adjustment">Adjustment (%)</label>
              <input id="rev-new-adjustment" type="number" step="0.01" value={adjustmentPct} onChange={(e) => setAdjustmentPct(e.target.value)} />
            </div>
          </>
        ) : (
          <div className="console-field">
            <label htmlFor="rev-new-clamp">{kind === "min_price" ? "Floor amount" : "Ceiling amount"}</label>
            <input id="rev-new-clamp" type="number" step="0.01" value={clampAmount} placeholder="e.g. 300.00" onChange={(e) => setClampAmount(e.target.value)} />
          </div>
        )}
      </div>
      <button type="submit" className="console-btn" disabled={busy}>{busy ? "Creating…" : "Create rule"}</button>
    </form>
  );
}

function RateSimulator({
  onError,
  currency,
}: {
  readonly onError: (m: UserMessageModel | null) => void;
  readonly currency: string;
}): ReactElement {
  const [baseRate, setBaseRate] = useState("150.00");
  const [occupancy, setOccupancy] = useState("80");
  const [leadTime, setLeadTime] = useState("14");
  const [dow, setDow] = useState("6");
  const [los, setLos] = useState("2");
  const [result, setResult] = useState<RateSimulationResult | null>(null);
  const [busy, setBusy] = useState(false);

  async function simulate(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const minor = parseMoneyToMinor(baseRate);
    if (minor === null) return;
    setBusy(true);
    try {
      const res = await apiSimulateRate({
        baseRateMinor: String(minor),
        occupancyPct: Number(occupancy || "0"),
        leadTimeDays: Number(leadTime || "0"),
        dayOfWeek: Number(dow || "0"),
        lengthOfStayNights: Number(los || "1"),
      });
      const err = asError(res);
      if (err) { onError(toUserMessage(err)); setResult(null); }
      else { onError(null); setResult(res as RateSimulationResult); }
    } catch (err) { onError(errModel(err)); }
    finally { setBusy(false); }
  }

  return (
    <div className="rev-panel">
      <h3 className="rev-panel__title">Rate simulator</h3>
      <p className="rev-muted">Pricing preview: apply every enabled rule + guardrail to a base rate.</p>
      <form className="console-form" onSubmit={simulate} noValidate>
        <div className="console-form__row">
          <div className="console-field">
            <label htmlFor="rev-sim-base">Base rate ({currency})</label>
            <input id="rev-sim-base" type="number" step="0.01" value={baseRate} onChange={(e) => setBaseRate(e.target.value)} />
          </div>
          <div className="console-field">
            <label htmlFor="rev-sim-occ">Occupancy (%)</label>
            <input id="rev-sim-occ" type="number" value={occupancy} onChange={(e) => setOccupancy(e.target.value)} />
          </div>
          <div className="console-field">
            <label htmlFor="rev-sim-lead">Lead time (days)</label>
            <input id="rev-sim-lead" type="number" value={leadTime} onChange={(e) => setLeadTime(e.target.value)} />
          </div>
        </div>
        <div className="console-form__row">
          <div className="console-field">
            <label htmlFor="rev-sim-dow">Day of week</label>
            <select id="rev-sim-dow" value={dow} onChange={(e) => setDow(e.target.value)}>
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d, i) => <option key={d} value={String(i)}>{d}</option>)}
            </select>
          </div>
          <div className="console-field">
            <label htmlFor="rev-sim-los">Length of stay (nights)</label>
            <input id="rev-sim-los" type="number" value={los} onChange={(e) => setLos(e.target.value)} />
          </div>
        </div>
        <button type="submit" className="console-btn" disabled={busy}>{busy ? "Simulating…" : "Simulate"}</button>
      </form>
      {result !== null && (
        <div className="rev-sim-result">
          <div><span className="rev-muted">Base</span><strong>{formatMoney(Number(result.baseRateMinor), currency)}</strong></div>
          <span className="rev-sim-arrow">→</span>
          <div><span className="rev-muted">Quoted</span><strong className="rev-sim-quoted">{formatMoney(Number(result.quotedRateMinor), currency)}</strong></div>
          <span className="rev-muted">{result.appliedRuleCount} rule(s) considered</span>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// Tab: Sync Health + Change History
// ===========================================================================

export function SyncHealthTab({
  propertyId,
  onError,
}: {
  readonly propertyId: string;
  readonly onError: (m: UserMessageModel | null) => void;
}): ReactElement {
  const [rows, setRows] = useState<readonly SyncHealthRow[]>([]);
  const [history, setHistory] = useState<readonly HistoryItem[]>([]);
  const [diag, setDiag] = useState<Record<string, unknown> | null>(null);
  const [tech, setTech] = useState(false);
  const [ariJobs, setAriJobs] = useState<{ rateJobs: readonly Record<string, unknown>[]; availabilityJobs: readonly Record<string, unknown>[] } | null>(null);
  const [reconOpen, setReconOpen] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Run-specific Full Reconciliation history (certification §8) + a sanitized
  // payload preview (certification §21). Both admin/dev technical validation.
  const [reconRuns, setReconRuns] = useState<readonly FullReconciliationRun[]>([]);
  const [preview, setPreview] = useState<PayloadPreview | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const reload = useCallback(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      apiSyncHealth(propertyId || null),
      apiRevenueHistory({ propertyId: propertyId || null, limit: 30 }),
      apiChannexDiagnostics(),
      apiFullReconciliationHistory(propertyId || null, 20),
    ])
      .then(([h, hist, d, runs]) => { if (!active) return; setRows(h); setHistory(hist); setDiag(asError(d) ? null : (d as unknown as Record<string, unknown>)); setReconRuns(runs); onError(null); })
      .catch((err) => { if (active) onError(errModel(err)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [propertyId, onError]);

  useEffect(() => reload(), [reload]);

  async function loadTech(): Promise<void> {
    setTech((v) => !v);
    if (ariJobs === null) {
      const res = await apiAriJobs();
      if (!asError(res)) setAriJobs(res as unknown as { rateJobs: Record<string, unknown>[]; availabilityJobs: Record<string, unknown>[] });
    }
  }
  async function retryFailed(): Promise<void> {
    setFlash("Retrying failed jobs…");
    await apiRetrySync();
    setFlash("Retry triggered.");
    reload();
  }
  async function showPreview(jobId: string): Promise<void> {
    const res = await apiPayloadPreview({ jobId });
    if (!asError(res)) { setPreview(res as PayloadPreview); setPreviewOpen(true); }
    else onError(toUserMessage(res as never));
  }

  const d = diag ?? {};
  function healthPill(v: unknown): ReactElement {
    const val = String(v ?? "unknown");
    const cls = val === "healthy" ? "ok" : val === "syncing" ? "pending" : val === "needs_attention" ? "attention" : "pending";
    return <span className={`avos-status avos-status--${cls}`}>{val === "needs_attention" ? "needs attention" : val}</span>;
  }

  if (loading && rows.length === 0) return <p className="rev-muted">Loading sync health…</p>;

  return (
    <div>
      {flash !== null && <p className="rev-flash" role="status">{flash}</p>}
      <div className="rev-panel">
        <div className="rev-panel__head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 className="rev-panel__title">Distribution health</h3>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button type="button" className="console-btn--ghost" onClick={() => void retryFailed()}>Retry failed</button>
            <button type="button" className="console-btn" onClick={() => setReconOpen(true)}>Full reconciliation</button>
          </div>
        </div>
        <div className="rev-synchealth">
          <div className="rev-synchealth__item"><span className="rev-muted">Availability</span> {healthPill(d.availabilitySyncHealth)}</div>
          <div className="rev-synchealth__item"><span className="rev-muted">Rates</span> {healthPill(d.ratesSyncHealth)}</div>
          <div className="rev-synchealth__item"><span className="rev-muted">Restrictions</span> {healthPill(d.ratesSyncHealth)}</div>
          <div className="rev-synchealth__item"><span className="rev-muted">Pending jobs</span> <strong>{Number(d.pendingAvailabilityJobs ?? 0) + Number(d.pendingRateJobs ?? 0)}</strong></div>
          <div className="rev-synchealth__item"><span className="rev-muted">Failed jobs</span> <strong>{Number(d.failedAvailabilityJobs ?? 0) + Number(d.failedRateJobs ?? 0)}</strong></div>
          <div className="rev-synchealth__item"><span className="rev-muted">Last availability sync</span> <strong>{d.lastAvailabilitySyncAt ? new Date(String(d.lastAvailabilitySyncAt)).toLocaleString() : "—"}</strong></div>
          <div className="rev-synchealth__item"><span className="rev-muted">Last rate/restriction sync</span> <strong>{d.lastRatesSyncAt ? new Date(String(d.lastRatesSyncAt)).toLocaleString() : "—"}</strong></div>
          <div className="rev-synchealth__item"><span className="rev-muted">Last full reconciliation</span> <strong>{d.lastFullReconciliationAt ? new Date(String(d.lastFullReconciliationAt)).toLocaleString() : "—"}</strong></div>
        </div>
        <button type="button" className="rev-link" onClick={() => void loadTech()} style={{ marginTop: "0.75rem" }}>
          {tech ? "Hide technical details" : "View technical details"}
        </button>
        {tech && (
          <div className="rev-tech">
            <p className="rev-muted rev-small">Channex property mappings: {String(d.mappedProperties ?? 0)} · Room types: {String(d.mappedRoomTypes ?? 0)} · Rate plans: {String(d.mappedRatePlans ?? 0)}</p>

            {/* RUN-SPECIFIC Full Reconciliation history (certification §8): each
                run keeps its OWN two task-id sets; a later update cannot
                overwrite the evidence. */}
            <h4 className="rev-small" style={{ margin: "0.75rem 0 0.25rem" }}>Full Reconciliation runs</h4>
            {reconRuns.length === 0 ? (
              <p className="rev-muted rev-small">No full reconciliation runs recorded yet.</p>
            ) : (
              <div className="rev-tablewrap">
                <table className="rev-table">
                  <thead><tr><th>Started</th><th>Horizon</th><th>Rooms</th><th>Rate plans</th><th>Availability task ID</th><th>Rates &amp; restrictions task ID</th><th>Status</th></tr></thead>
                  <tbody>
                    {reconRuns.map((r) => (
                      <tr key={r.id}>
                        <td>{r.startedAt ? new Date(r.startedAt).toLocaleString() : "—"}</td>
                        <td>{r.horizonDays} days</td>
                        <td>{r.rooms}</td>
                        <td>{r.ratePlans}</td>
                        <td className="rev-small">{r.availabilityTaskIds.length > 0 ? r.availabilityTaskIds.join(", ") : "—"}</td>
                        <td className="rev-small">{r.ratesTaskIds.length > 0 ? r.ratesTaskIds.join(", ") : "—"}</td>
                        <td>{r.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Batch + delta job history (certification §9): per-operation scope
                + its OWN task id, with a sanitized payload preview (§21). */}
            <h4 className="rev-small" style={{ margin: "0.75rem 0 0.25rem" }}>Recent sync operations</h4>
            {ariJobs && (
              <div className="rev-tablewrap">
                <table className="rev-table">
                  <thead><tr><th>Type</th><th>Scope</th><th>Status</th><th>Attempts</th><th>Next retry</th><th>Task ID</th><th>Payload</th></tr></thead>
                  <tbody>
                    {([...(ariJobs.rateJobs ?? []).map((j) => ({ ...j, _t: "Rates/Restr." })), ...(ariJobs.availabilityJobs ?? []).map((j) => ({ ...j, _t: "Availability" }))] as Record<string, unknown>[]).slice(0, 30).map((j, i) => (
                      <tr key={String(j.id ?? i)}>
                        <td>{String(j._t)}</td>
                        <td>{String(j.scope ?? "—")}</td>
                        <td>{String(j.status ?? "—")}</td>
                        <td>{String(j.attempts ?? 0)}</td>
                        <td>{j.nextRetry ? new Date(String(j.nextRetry)).toLocaleTimeString() : "—"}</td>
                        <td className="rev-small">{Array.isArray(j.taskIds) && (j.taskIds as string[]).length > 0 ? (j.taskIds as string[])[0] : "—"}</td>
                        <td>{String(j._t) === "Rates/Restr." && j.id ? (
                          <button type="button" className="rev-link" onClick={() => void showPreview(String(j.id))}>Preview</button>
                        ) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {previewOpen && preview && <PayloadPreviewModal preview={preview} onClose={() => setPreviewOpen(false)} />}

      {reconOpen && <FullReconciliationModal propertyId={propertyId} onClose={() => setReconOpen(false)} onDone={() => { setReconOpen(false); reload(); }} onError={onError} />}

      <div className="rev-panel">
        <h3 className="rev-panel__title">Channel Manager sync</h3>
        <div className="rev-tablewrap">
          <table className="rev-table">
            <thead>
              <tr><th scope="col">Property</th><th scope="col">Health</th><th scope="col">Last activity</th><th scope="col">Last operation</th></tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={4} className="rev-muted">No provisioned properties.</td></tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.propertyId}>
                    <td>{r.propertyName}</td>
                    <td><span className={`avos-status avos-status--${r.health === "healthy" ? "ok" : r.health === "warning" ? "attention" : r.health === "error" ? "critical" : "pending"}`}>{r.health}</span></td>
                    <td>{r.lastActivityAt ? new Date(r.lastActivityAt).toLocaleString() : "—"}</td>
                    <td>{r.lastOperation ?? "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rev-panel">
        <h3 className="rev-panel__title">Change history</h3>
        <div className="rev-tablewrap">
          <table className="rev-table">
            <thead>
              <tr><th scope="col">When</th><th scope="col">Property</th><th scope="col">Operation</th><th scope="col">Status</th></tr>
            </thead>
            <tbody>
              {history.length === 0 ? (
                <tr><td colSpan={4} className="rev-muted">No recent activity.</td></tr>
              ) : (
                history.map((h, i) => (
                  <tr key={`${h.at}-${i}`}>
                    <td>{new Date(h.at).toLocaleString()}</td>
                    <td>{h.propertyName}</td>
                    <td>{h.operation}</td>
                    <td><span className={`avos-status avos-status--${h.status === "ok" ? "ok" : h.status === "warning" ? "attention" : h.status === "error" ? "critical" : "pending"}`}>{h.status}</span></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/**
 * Sanitized payload preview modal (certification §21): shows the operation,
 * room(s), rate plan(s), date range(s), changed fields, compaction + value
 * count, and the task id(s) for an ARI operation. NEVER shows API keys/secrets
 * or raw auth — only a technical summary so we never submit the wrong task.
 */
function PayloadPreviewModal({
  preview,
  onClose,
}: {
  readonly preview: PayloadPreview;
  readonly onClose: () => void;
}): ReactElement {
  return (
    <div className="rev-modal" role="dialog" aria-modal="true" aria-label="Channex payload preview">
      <div className="rev-modal__backdrop" onClick={onClose} />
      <div className="rev-modal__panel">
        <h3 className="rev-modal__title">Payload preview · {preview.operation}</h3>
        <p className="rev-muted rev-small">A sanitized summary of what AVOS sends to Channex. No API keys or secrets are shown.</p>
        <div className="rev-synchealth">
          <div className="rev-synchealth__item"><span className="rev-muted">Room(s)</span> <strong>{preview.rooms.join(", ") || "—"}</strong></div>
          <div className="rev-synchealth__item"><span className="rev-muted">Rate plan(s)</span> <strong>{preview.ratePlans.join(", ") || "—"}</strong></div>
          <div className="rev-synchealth__item"><span className="rev-muted">Fields changed</span> <strong>{preview.changedFields.join(", ") || "—"}</strong></div>
          <div className="rev-synchealth__item"><span className="rev-muted">Values</span> <strong>{preview.inputValueCount} day(s) → {preview.serializedValueCount} range(s)</strong></div>
          <div className="rev-synchealth__item"><span className="rev-muted">Task ID(s)</span> <strong className="rev-small">{preview.taskIds.length > 0 ? preview.taskIds.join(", ") : "—"}</strong></div>
        </div>
        <div className="rev-tablewrap" style={{ marginTop: "0.75rem" }}>
          <table className="rev-table">
            <thead><tr><th>Date range</th><th>Fields</th></tr></thead>
            <tbody>
              {preview.values.map((v, i) => (
                <tr key={`${v.rate_plan_id}-${i}`}>
                  <td className="rev-small">{v.dateRange}</td>
                  <td className="rev-small">{Object.entries(v.fields).map(([k, val]) => `${k}=${String(val)}`).join(", ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="rev-modal__actions">
          <button type="button" className="console-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

/** Full Reconciliation modal (§26-28): validate → 2-call 500-day sync → task IDs. */
function FullReconciliationModal({
  propertyId,
  onClose,
  onDone,
  onError,
}: {
  readonly propertyId: string;
  readonly onClose: () => void;
  readonly onDone: () => void;
  readonly onError: (m: UserMessageModel | null) => void;
}): ReactElement {
  const [phase, setPhase] = useState<"confirm" | "running" | "done" | "blocked">("confirm");
  const [result, setResult] = useState<Record<string, unknown> | null>(null);
  const [issues, setIssues] = useState<readonly { kind: string; name: string }[]>([]);

  async function start(): Promise<void> {
    if (propertyId === "") { onError({ what: "Select a property", why: "Full reconciliation runs per property.", next: "Choose a property in the header selector." }); return; }
    setPhase("running");
    try {
      const res = await apiFullReconciliation(propertyId);
      if (asError(res)) { onError(toUserMessage(res as never)); setPhase("confirm"); return; }
      const r = res as unknown as Record<string, unknown>;
      const mi = (r.mappingIssues as { kind: string; name: string }[]) ?? [];
      if (mi.length > 0) { setIssues(mi); setPhase("blocked"); return; }
      setResult(r); setPhase("done"); onDone();
    } catch (err) { onError(errModel(err)); setPhase("confirm"); }
  }

  return (
    <div className="rev-modal" role="dialog" aria-modal="true" aria-label="Full reconciliation">
      <div className="rev-modal__backdrop" onClick={phase === "running" ? undefined : onClose} />
      <div className="rev-modal__panel">
        <h3 className="rev-modal__title">Full reconciliation</h3>
        {phase === "confirm" && (
          <>
            <p className="rev-muted">This will resend authoritative availability, rates and restrictions for all mapped rooms and rate plans for the next 500 days. Use this for initial setup, recovery or troubleshooting.</p>
            <div className="rev-modal__actions">
              <button type="button" className="console-btn--ghost" onClick={onClose}>Cancel</button>
              <button type="button" className="console-btn" onClick={() => void start()}>Start reconciliation</button>
            </div>
          </>
        )}
        {phase === "running" && <p className="rev-muted">Sending availability, then rates &amp; restrictions… (2 requests)</p>}
        {phase === "blocked" && (
          <>
            <p className="rev-error">Cannot complete reconciliation — {issues.length} mapping(s) require attention.</p>
            <ul>{issues.map((i, n) => <li key={n}>{i.name} — {i.kind} mapping missing</li>)}</ul>
            <div className="rev-modal__actions"><button type="button" className="console-btn--ghost" onClick={onClose}>Close</button></div>
          </>
        )}
        {phase === "done" && result && (
          <>
            <p className="rev-flash" role="status">Reconciliation complete.</p>
            <dl className="rev-recon-summary">
              <div><dt>Days</dt><dd>{String(result.horizonDays ?? 500)}</dd></div>
              <div><dt>Rooms</dt><dd>{String(result.rooms ?? 0)}</dd></div>
              <div><dt>Rate plans</dt><dd>{String(result.ratePlans ?? 0)}</dd></div>
              <div><dt>Availability task ID</dt><dd className="rev-small">{Array.isArray(result.availabilityTaskIds) && (result.availabilityTaskIds as string[]).length > 0 ? (result.availabilityTaskIds as string[]).join(", ") : "—"}</dd></div>
              <div><dt>Rates &amp; restrictions task ID</dt><dd className="rev-small">{Array.isArray(result.ratesTaskIds) && (result.ratesTaskIds as string[]).length > 0 ? (result.ratesTaskIds as string[]).join(", ") : "—"}</dd></div>
              <div><dt>Run ID</dt><dd className="rev-small">{result.runId ? String(result.runId) : "—"}</dd></div>
            </dl>
            <p className="rev-muted rev-small">This run is recorded in Technical Details → Full Reconciliation runs with its own task IDs.</p>
            <div className="rev-modal__actions"><button type="button" className="console-btn" onClick={onClose}>Done</button></div>
          </>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// Root screen
// ===========================================================================

/**
 * A tab panel renderer — renders the ONE existing tab component for a key. The
 * underlying tab components (Overview / Inventory / Rate Calendar / Rate Plans /
 * Restrictions / Pricing Rules / Sync Health) are UNCHANGED and reused as-is;
 * this only selects which of them to mount for a given workspace.
 */
function renderWorkspaceTab(
  tab: TabKey,
  propertyId: string,
  properties: readonly Property[],
  onError: (m: UserMessageModel | null) => void,
): ReactElement | null {
  switch (tab) {
    case "overview": return <OverviewTab propertyId={propertyId} onError={onError} />;
    case "inventory": return <InventoryTab propertyId={propertyId} onError={onError} />;
    case "calendar": return <RateCalendarTab propertyId={propertyId} onError={onError} />;
    case "rate-plans": return <RatePlansTab propertyId={propertyId} onError={onError} />;
    case "restrictions": return <RestrictionsTab propertyId={propertyId} onError={onError} />;
    case "rules": return <PricingRulesTab propertyId={propertyId} properties={properties} onError={onError} />;
    case "sync": return <SyncHealthTab propertyId={propertyId} onError={onError} />;
    default: return null;
  }
}

/**
 * The shared workspace shell (property selector + tab bar + panel). Information-
 * architecture reorg: the former single "Revenue & Rates" screen is split into
 * two compositions of the SAME tab components over this ONE shell —
 * {@link RevenueScreen} (business performance: Overview) and
 * {@link InventoryRatesScreen} (operations: Inventory / Rate Calendar / Rate
 * Plans / Restrictions / Pricing Rules). The property-selector behaviour, tab
 * styling, auto-select-sole-property, and per-tab components are identical to
 * before — only the set of tabs shown differs.
 */
function WorkspaceScreen({
  title,
  subtitle,
  ariaId,
  tabsLabel,
  tabKeys,
}: {
  readonly title: string;
  readonly subtitle: string;
  readonly ariaId: string;
  readonly tabsLabel: string;
  readonly tabKeys: readonly TabKey[];
}): ReactElement {
  const [properties, setProperties] = useState<readonly Property[]>([]);
  const [propertyId, setPropertyId] = useState<string>("");
  const [tab, setTab] = useState<TabKey>(tabKeys[0] ?? "overview");
  const [apiError, setApiError] = useState<UserMessageModel | null>(null);
  const seeded = useRef(false);

  const visibleTabs = TABS.filter((t) => tabKeys.includes(t.key));

  // Load properties + auto-select the sole property (persists across tabs).
  useEffect(() => {
    let active = true;
    apiListProperties()
      .then((ps) => {
        if (!active) return;
        setProperties(ps);
        if (!seeded.current && ps.length === 1) { setPropertyId(ps[0]!.id); }
        seeded.current = true;
      })
      .catch(() => undefined);
    return () => { active = false; };
  }, []);

  const onError = useCallback((m: UserMessageModel | null) => setApiError(m), []);

  return (
    <section className="rev-screen" aria-labelledby={ariaId}>
      <header className="rev-header">
        <div>
          <h1 id={ariaId} className="console-main__title">{title}</h1>
          <p className="rev-subtitle">{subtitle}</p>
        </div>
        <div className="console-field rev-propsel">
          <label htmlFor="rev-property">Property</label>
          <select id="rev-property" value={propertyId} onChange={(e) => setPropertyId(e.target.value)}>
            <option value="">All properties</option>
            {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
      </header>

      {visibleTabs.length > 1 && (
        <nav className="rev-tabs" aria-label={tabsLabel}>
          {visibleTabs.map((t) => (
            <button
              key={t.key}
              type="button"
              className={t.key === tab ? "rev-tab rev-tab--active" : "rev-tab"}
              aria-current={t.key === tab ? "page" : undefined}
              onClick={() => { setTab(t.key); setApiError(null); }}
            >
              {t.label}
            </button>
          ))}
        </nav>
      )}

      {apiError !== null && <div className="rev-error"><UserMessage message={apiError} /></div>}

      <div className="rev-tabpanel">
        {renderWorkspaceTab(tab, propertyId, properties, onError)}
      </div>
    </section>
  );
}

/**
 * REVENUE — business performance only (Overview: gross revenue, occupancy,
 * units, property performance). Operational inventory/rate-management tabs now
 * live in {@link InventoryRatesScreen}; Sync Health lives in the Channel
 * Manager. Reuses the SAME OverviewTab component and APIs — no logic change.
 */
export default function RevenueScreen(): ReactElement {
  return (
    <WorkspaceScreen
      title="Revenue"
      subtitle="Portfolio performance: gross revenue, occupancy, and per-property results."
      ariaId="rev-title"
      tabsLabel="Revenue workspace"
      tabKeys={REVENUE_TAB_KEYS}
    />
  );
}

/**
 * INVENTORY & RATES — the operational rate/inventory management workspace
 * (Inventory, Rate Calendar, Rate Plans, Restrictions, Pricing Rules). Reuses
 * the SAME tab components, APIs, state and property selector that previously
 * lived under Revenue & Rates — no behaviour change, only relocation.
 */
export function InventoryRatesScreen(): ReactElement {
  return (
    <WorkspaceScreen
      title="Inventory & Rates"
      subtitle="Manage inventory, the rate calendar, rate plans, restrictions, pricing rules and sync health. Changes sync to your channels automatically."
      ariaId="rev-title"
      tabsLabel="Inventory & Rates workspace"
      tabKeys={INVENTORY_RATES_TAB_KEYS}
    />
  );
}
